import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Printer, Wifi, WifiOff, Check, Download } from "lucide-react";
import { useTranslation } from "../i18n";
import { fetchNetworkPrinters, saveNetworkPrinter, getSavedNetworkPrinter, type NetworkPrinter } from "./helpers/networkPrint";
import { getPrintAgentStatus, isAgentOutdated, PRINT_AGENT_DOWNLOAD_URL, EXPECTED_AGENT_VERSION, resolvePrintAgentDownloadUrl, getLocalPrinters, getSavedLocalPrinter, saveLocalPrinter, clearLocalPrinter, savePrintModePreference, clearPrintModePreference } from "./helpers/printAgent";
import FlowLoader from "./FlowLoader";

interface PrinterSelectModalProps {
    autoPrint: boolean;
    printMode: "local" | "network" | null;
    onSelect: (printer: { agentId: string; printerName: string; friendlyName: string; warehouse: string }) => void;
    onConnectLocal: () => void;
    onDisconnect: () => void;
    onClose: () => void;
    /** When provided, renders a controlled on/off "Auto print" toggle reflecting
     *  `autoPrintEnabled` and flips it on click. This is additive — callers that
     *  don't pass it keep the original disconnect-style toggle behaviour. */
    autoPrintEnabled?: boolean;
    onToggleAutoPrint?: (next: boolean) => void;
}

const PrinterSelectModal = ({ autoPrint, printMode, onSelect, onConnectLocal, onDisconnect, onClose, autoPrintEnabled, onToggleAutoPrint }: PrinterSelectModalProps) => {
    const { t } = useTranslation();
    const [printers, setPrinters] = useState<NetworkPrinter[]>([]);
    const [loading, setLoading] = useState(true);
    const [localAvailable, setLocalAvailable] = useState(false);
    const [localPrinters, setLocalPrinters] = useState<string[]>([]);
    const [localVersion, setLocalVersion] = useState<string | null>(null);
    const [agentNeedsUpdate, setAgentNeedsUpdate] = useState(false);
    // Staging FE (always the newest build) when reachable, else this origin.
    const [downloadUrl, setDownloadUrl] = useState(PRINT_AGENT_DOWNLOAD_URL);
    const savedPrinter = getSavedNetworkPrinter();
    const savedLocalPrinter = getSavedLocalPrinter();

    useEffect(() => {
        resolvePrintAgentDownloadUrl().then(setDownloadUrl);
    }, []);

    // Get the current user's warehouse to filter the printer list
    const userData = window.localStorage.getItem("user");
    const userWarehouse = userData ? (JSON.parse(userData).warehouse || "") : "";

    useEffect(() => {
        Promise.all([
            fetchNetworkPrinters(),
            getPrintAgentStatus(),
            getLocalPrinters(),
        ]).then(([p, agentStatus, local]) => {
            // Only show printers from the user's warehouse
            const filtered = userWarehouse
                ? p.filter(printer => printer.warehouse === userWarehouse)
                : p;
            setPrinters(filtered);
            if (agentStatus && agentStatus.status === "ok") {
                setLocalAvailable(true);
                setLocalVersion(agentStatus.version ?? null);
                // Hide Windows' virtual printers — same exclusions the agent
                // applies when picking its default target.
                setLocalPrinters(local.printers.filter(n => !/Microsoft Print to PDF|OneNote|XPS|Fax/i.test(n)));
                if (isAgentOutdated(agentStatus.version)) {
                    setAgentNeedsUpdate(true);
                }
            } else {
                setLocalAvailable(false);
            }
            setLoading(false);
        });
    }, []);

    // Group by warehouse
    const grouped: Record<string, NetworkPrinter[]> = {};
    printers.forEach(p => {
        if (!grouped[p.warehouse]) grouped[p.warehouse] = [];
        grouped[p.warehouse].push(p);
    });

    const handleSelect = (printer: NetworkPrinter) => {
        const selection = {
            agentId: printer.agentId,
            printerName: printer.systemName,
            friendlyName: printer.friendlyName,
            warehouse: printer.warehouse,
        };
        saveNetworkPrinter(selection);
        savePrintModePreference("network");
        onSelect(selection);
    };

    const isSelectedNetwork = (printer: NetworkPrinter) =>
        printMode === "network" && savedPrinter?.agentId === printer.agentId && savedPrinter?.printerName === printer.systemName;

    // Portalled to <body>: the modal is opened from inside transformed
    // containers (e.g. the dispatch bottom bar's translateX centring), and a
    // CSS transform re-bases position:fixed descendants — without the portal
    // the overlay anchors to that ancestor and the card runs off-screen.
    return createPortal(
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-card printer-select-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-card-header">
                    <h2>{t("select_printer")}</h2>
                    <button className="modal-close" onClick={onClose}><X size={18} /></button>
                </div>
                <div className="modal-card-body">
                    {/* Controlled auto-print toggle (Wipe module) — a real on/off
                        preference, distinct from the disconnect-style toggle below. */}
                    {onToggleAutoPrint && (
                        <div className="printer-select-autoprint">
                            <div className="printer-select-autoprint-info">
                                <Printer size={18} />
                                <span>{t("auto_print")}</span>
                            </div>
                            <div
                                className={`dispatch-toggle ${autoPrintEnabled ? "dispatch-toggle--on" : ""}`}
                                onClick={() => onToggleAutoPrint(!autoPrintEnabled)}
                                style={{ cursor: "pointer" }}
                            >
                                <div className="dispatch-toggle-knob" />
                            </div>
                        </div>
                    )}
                    {/* Auto-print toggle (legacy disconnect-style) */}
                    {!onToggleAutoPrint && autoPrint && (
                        <div className="printer-select-autoprint">
                            <div className="printer-select-autoprint-info">
                                <Printer size={18} />
                                <span>{t("auto_print")}</span>
                                {printMode === "local" && <span className="printer-select-mode-badge">{t("local")}</span>}
                                {printMode === "network" && savedPrinter && (
                                    <span className="printer-select-mode-badge">{savedPrinter.friendlyName}</span>
                                )}
                            </div>
                            <div
                                className="dispatch-toggle dispatch-toggle--on"
                                onClick={() => { clearPrintModePreference(); onDisconnect(); }}
                                style={{ cursor: "pointer" }}
                            >
                                <div className="dispatch-toggle-knob" />
                            </div>
                        </div>
                    )}

                    {loading ? (
                        <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
                            <FlowLoader />
                        </div>
                    ) : (
                        <>
                            {/* Local printers — one row per printer the agent on
                                THIS machine exposes, so the user can target e.g.
                                the label printer rather than the Windows default.
                                Falls back to a single default-printer row when the
                                list can't be fetched (old agent mid-request etc.). */}
                            {localAvailable && (
                                <>
                                    <p className="printer-select-warehouse">{t("local")}</p>
                                    {localPrinters.length > 0 ? (
                                        localPrinters.map(name => {
                                            const active = printMode === "local" && savedLocalPrinter === name;
                                            return (
                                                <button
                                                    key={name}
                                                    className={`printer-select-row ${active ? "printer-select-row--active" : ""}`}
                                                    onClick={() => { saveLocalPrinter(name); savePrintModePreference("local"); onConnectLocal(); }}
                                                >
                                                    <div className="printer-select-icon">
                                                        <Printer size={20} />
                                                    </div>
                                                    <div className="printer-select-info">
                                                        <p className="printer-select-name">{name}</p>
                                                        <p className="printer-select-host">{t("this_device")}</p>
                                                    </div>
                                                    {active && <Check size={18} color="var(--color-success)" />}
                                                </button>
                                            );
                                        })
                                    ) : (
                                        <button
                                            className={`printer-select-row ${printMode === "local" ? "printer-select-row--active" : ""}`}
                                            onClick={() => { clearLocalPrinter(); savePrintModePreference("local"); onConnectLocal(); }}
                                        >
                                            <div className="printer-select-icon">
                                                <Printer size={20} />
                                            </div>
                                            <div className="printer-select-info">
                                                <p className="printer-select-name">{t("local_printer")}</p>
                                                <p className="printer-select-host">{t("this_device")}</p>
                                            </div>
                                            {printMode === "local" && <Check size={18} color="var(--color-success)" />}
                                        </button>
                                    )}
                                </>
                            )}

                            {/* Network printers */}
                            {printers.length > 0 ? (
                                <div className="printer-select-list">
                                    {Object.entries(grouped).map(([warehouse, wPrinters]) => (
                                        <div key={warehouse}>
                                            <p className="printer-select-warehouse">{warehouse}</p>
                                            {wPrinters.map(printer => (
                                                <button
                                                    key={printer._id}
                                                    className={`printer-select-row ${isSelectedNetwork(printer) ? "printer-select-row--active" : ""}`}
                                                    onClick={() => handleSelect(printer)}
                                                    disabled={printer.status === "offline"}
                                                >
                                                    <div className="printer-select-icon">
                                                        <Printer size={20} />
                                                    </div>
                                                    <div className="printer-select-info">
                                                        <p className="printer-select-name">{printer.friendlyName}</p>
                                                        <p className="printer-select-host">{printer.hostname}</p>
                                                    </div>
                                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                        {isSelectedNetwork(printer) && <Check size={18} color="var(--color-success)" />}
                                                        <div className={`printer-select-status ${printer.status === "online" ? "printer-select-status--online" : "printer-select-status--offline"}`}>
                                                            {printer.status === "online" ? <Wifi size={14} /> : <WifiOff size={14} />}
                                                        </div>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            ) : !localAvailable ? (
                                <div style={{ textAlign: "center", padding: "24px 0", color: "var(--color-text-muted)" }}>
                                    <Printer size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
                                    <p style={{ fontSize: "0.95rem", fontWeight: 600 }}>{t("no_printers_available")}</p>
                                </div>
                            ) : null}

                            {/* Print agent state — desktop only, at the bottom.
                                Not installed → download link (lets non-admin-page
                                users grab the installer for a first install).
                                Installed + current → static "installed" row with
                                a tick and the version.
                                Installed + outdated → download link again: agents
                                that old predate self-update, so without a manual
                                install they'd stay outdated forever. */}
                            {!/Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) && (
                                <>
                                    <hr style={{ border: "none", borderTop: "1px solid var(--color-border)", margin: "16px 0 12px" }} />
                                    {localAvailable && !agentNeedsUpdate ? (
                                        <div className="printer-select-row printer-select-row--static">
                                            <div className="printer-select-icon printer-select-icon--ok">
                                                <Check size={20} />
                                            </div>
                                            <div className="printer-select-info">
                                                <p className="printer-select-name">{t("print_agent_installed")}</p>
                                                <p className="printer-select-host">
                                                    {localVersion ? `v${localVersion}` : t("this_device")}
                                                </p>
                                            </div>
                                        </div>
                                    ) : (
                                        <a
                                            href={downloadUrl}
                                            className="printer-select-row"
                                            style={{ textDecoration: "none", color: "inherit", boxSizing: "border-box" }}
                                        >
                                            <div className="printer-select-icon">
                                                <Download size={20} />
                                            </div>
                                            <div className="printer-select-info">
                                                <p className="printer-select-name">{t("download_print_agent")}</p>
                                                <p className="printer-select-host">
                                                    {localAvailable
                                                        ? `${t("print_agent_update_available")} (${localVersion ? `v${localVersion}` : "?"} → v${EXPECTED_AGENT_VERSION})`
                                                        : t("print_agent_not_installed")}
                                                </p>
                                            </div>
                                        </a>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default PrinterSelectModal;
